import {inject, Injectable} from '@angular/core'
import {OpenLayersMapService} from './open-layers-map.service'
import {createEmpty, extend, getCenter, isEmpty} from 'ol/extent'
import {Map} from 'ol'
import VectorSource from 'ol/source/Vector'

@Injectable({
    providedIn: 'root',
})
export class OpenLayersMapZoomService {
    openLayersMapService: OpenLayersMapService

    constructor() {
        this.openLayersMapService = inject(OpenLayersMapService)
    }

    zoomMapToAllFeaturesOfVectorSource(map: Map, vectorSource: VectorSource<any>, padding: number): void {
        const zoomArea = createEmpty()

        this._extendFeatures(vectorSource, zoomArea)
        this._setDefaultMapCenter(zoomArea, map)

        map.updateSize()

        if (this.isValidExtent(zoomArea)) {
            map.getView().fit(zoomArea, {
                padding: [padding, padding, padding, padding], //zoom out so user sees parcel surroundings
                nearest: true, //disable fractional zoom
            })
        } else {
            console.warn('Extent is invalid or empty:', zoomArea)
        }
    }

    zoomMapToFeatureIdOfVectorSource(featureId: number, vectorSource: VectorSource<any>, map: Map, padding: number): void {
        let zoomArea: number[] = createEmpty()
        let duration: number

        if (!featureId || featureId === Infinity) {
            this._extendFeatures(vectorSource, zoomArea)
            duration = 200
        } else {
            vectorSource.forEachFeature((feature) => {
                if (feature.get('id') === featureId && feature.getGeometry()) {
                    zoomArea = feature.getGeometry().getExtent()
                }
            })
            duration = 400
        }

        // When map was hidden and gets rendered (e.g. after switching tab) animation is unwanted
        if (map?.getSize() === undefined || map?.getSize()[0] === 0 || map?.getSize()[1] === 0) {
            duration = 0
        }

        map.updateSize()

        if (zoomArea && !isEmpty(zoomArea)) {
            map.getView().fit(zoomArea, {
                padding: [padding, padding, padding, padding], //zoom out so user sees parcel surroundings
                nearest: true, //disable fractional zoom
                duration: duration,
            })
        }
    }

    zoomMapToMultipleLayers(map: Map, layerNames: string[], padding: number): void {
        let zoomArea: number[] = createEmpty()

        layerNames.forEach(layerName => {
            const vectorSource = this.openLayersMapService.getVectorSourceForLayerByName(layerName, map)

            if (vectorSource) {
                vectorSource.getFeatures().forEach(feature => {
                    const featureGeometry = feature.getGeometry()
                    if (featureGeometry) {
                        extend(zoomArea, feature.getGeometry().getExtent())
                    }
                })
            }
        })

        map.updateSize()

        if (zoomArea && !isEmpty(zoomArea)) {

            this._setDefaultMapCenter(zoomArea, map)

            map.getView().fit(zoomArea, {
                padding: [padding, padding, padding, padding], //zoom out so user sees parcel surroundings
                nearest: true, //disable fractional zoom
                duration: 200,
            })
        }
    }

    ///////////////////////////////////////////////////

    private _extendFeatures(vectorSource: VectorSource<any>, zoomArea: number[]): void {
        vectorSource.getFeatures().forEach(feature => {
            if (feature?.getGeometry()) {
                extend(zoomArea, feature.getGeometry().getExtent())
            }
        })
    }

    private _setDefaultMapCenter(zoomArea: number[], map: Map): void {
        if (zoomArea && !isEmpty(zoomArea)) {
            const zoomCenter: number[] = getCenter(zoomArea)

            if (map && zoomCenter) {
                map.getView().set('defaultViewCenter', zoomCenter)
            }
        }
    }

    private isValidExtent(extent: number[]): boolean {
        return (
            Array.isArray(extent) && extent.length === 4 && extent.every((val) => Number.isFinite(val)) && !isEmpty(extent)
        )
    }
}